import Constants from 'api-constants'
import ConfigHelper from 'framework/helpers/config'
import DataHelper from 'framework/helpers/data'
import { serialize } from 'helpers/index.js'
import { of, throwError } from 'rxjs'
import { catchError, mergeMap } from 'rxjs/operators'
import VoltError from 'VoltError'
import Fetch from '../fetch'

export default class PinApi extends Fetch {
    constructor(config = {}, otherApis = {}) {
        super(config, otherApis)
    }

    /** @type {String} nagra API url */
    _nagraApiUrl = ConfigHelper.getInstance().getConfig('urls').apiUrl

    /** @type {PinType} */
    static TYPE = {
        MASTER: 'PURCHASE',
        ADULT: 'PARENTAL',
        CENSORSHIP: 'CENSORSHIP',
    }

    /**
     * Returns pin types
     * @returns {PinType}
     */
    getPinTypes = () => {
        return PinApi.TYPE
    }

    /**
     * Returns pin type as a proper string for request
     * @param {PinType} type - types from {@link PinType}
     * @returns {String} pin type string for request
     */
    _getPinType(type) {
        switch (type) {
            case PinApi.TYPE.ADULT:
                return 'ParentalPin'
            case PinApi.TYPE.MASTER:
                return 'PurchasePin'
            case PinApi.TYPE.CENSORSHIP:
                return 'R21Pin'
            default:
                break
        }
    }

    /**
     * Returns standard headers for pinApi request
     * @returns {RequestHeaders} headers for request
     */
    _getRequestHeaders() {
        return {
            'Content-Type': Constants.httpHeader.ContentType.APPLICATION_JSON,
            'nv-tenant-id': ConfigHelper.getInstance().getConfig('nagra').tenantId,
        }
    }

    /**
     * Returns nagra access token for pinApi
     * @returns {Observable<String>|Observable<VoltError>} nagra access token or {@link VoltError} in case of token is empty
     */
    _getAccessToken = () => {
        this.logger.info('Getting access token...')
        const accessToken = DataHelper.getInstance().getSecondaryAccessToken()
        if (!accessToken) {
            const error = 'Cannot get profile due to empty Access Token'
            this.logger.error('PinApi', error)
            return throwError(
                new VoltError(VoltError.codes.INVALID_AUTH_TOKEN, { extraLog: error })
            )
        }
        return of(accessToken)
    }

    /**
     * Checks if a given PIN code of a given type is valid.
     * @param {String} pin The PIN code that needs validating
     * @param {PinType} type The type of PIN code (ex: purchase, parental control) look {@link PinType}
     * @return {Observable<Boolean>} The validity of the given PIN code
     */
    validatePin(pin, type) {
        this.logger.info('Validating pin...')

        // Verify the presence of the Pin before otherwise if the PIN does not exist return an authentication error
        // Not necessary to verify that the PIN exists for verification. The API should be account of this
        return this._getAccessToken().pipe(
            mergeMap((token) => {
                return this.fetch({
                    url: `${this._nagraApiUrl}/adm/v1/user/accounts/actions/validatePin${serialize({
                        token,
                    })}`,
                    headers: this._getRequestHeaders(),
                    body: {
                        type: this._getPinType(type),
                        value: pin,
                    },
                    method: 'POST',
                    log: 'VALIDATE_PIN',
                })
            }),
            mergeMap(() => of(true)),
            catchError((error) => {
                if (error.code === VoltError.codes.USER_PIN_UNSET.code) {
                    if (ConfigHelper.getInstance().getConfig().defaultPinCode === pin) {
                        this.logger.warn('PinApi', 'Use default pin code as pin does not exist')
                        return of(true)
                    }
                }
                return throwError(
                    new VoltError(VoltError.codes.INVALID_PIN, { inheritedError: error })
                )
            })
        )
    }

    /**
     * Returns only user's PINs from '/adm/v1/user/accounts/pins' response
     * @param {GetUserPinResponse|NagraSpecificBackendError} response
     * @returns {Observable<Array<UserPinData>>} returns user's pin
     */
    _returnUserPins = ({ response } = {}) => {
        return of(response.resourceSet)
    }

    /**
     * Returns user's PIN
     * @param {PinType} type - not necessary now but will use in future
     * @returns {Observable<Array<UserPinData>>}
     */
    getPins(type = '') {
        this.logger.info('Getting pins...')

        return this._getAccessToken().pipe(
            mergeMap((token) => {
                return this.fetch({
                    url: `${this._nagraApiUrl}/adm/v1/user/accounts/pins${serialize({ token })}`,
                    headers: this._getRequestHeaders(),
                    method: 'GET',
                    log: 'GET_PINS',
                })
            }),
            mergeMap(this._returnUserPins)
        )
    }

    /**
     * Checks if user has a PIN with a given type
     * @param {Array<UserPinData>} pins - user's PINs
     * @param {PinType} type - required PIN's type
     * @returns {Observable<Boolean>|Observable<VoltError>} returns true if user has PIN with required type
     *  otherwise returns {@link VoltError}
     */
    _isPinWithTypeExist(pins = [], type = '') {
        if (pins.length === 0) {
            const error = 'No PINs available for a user'
            this.logger.error('PinApi', error)
            return throwError(new VoltError(VoltError.codes.USER_PIN_UNSET, { extraLog: error }))
        }
        const isPinTypeExist = pins.some((pin) => pin.type === this._getPinType(type))
        if (!isPinTypeExist) {
            const error = 'No such PIN type for a user exists'
            this.logger.error('PinApi', error)
            return throwError(new VoltError(VoltError.codes.USER_PIN_UNSET, { extraLog: error }))
        }
        return of(true)
    }

    /**
     * Updates given user's pin
     * Also checks if a given PIN type connected with a user
     * @param {String} pin - current user's pin
     * @param {String} newPin - preferred new user's pin
     * @param {PinType} type - {@link PinType}
     * @returns {Observable<Boolean>} returns true in case of success
     */
    updatePin(type, newPin, oldPin) {
        this.logger.info('Updating pin...')

        return this.getPins().pipe(
            mergeMap((pins) => this._isPinWithTypeExist(pins, type)),
            mergeMap(this._getAccessToken),
            mergeMap((token) => {
                return this.fetch({
                    url: `${this._nagraApiUrl}/adm/v1/user/accounts/actions/changePin${serialize({
                        token,
                    })}`,
                    headers: this._getRequestHeaders(),
                    body: {
                        type: this._getPinType(type),
                        value: oldPin,
                        newValue: newPin,
                    },
                    method: 'PUT',
                    log: 'UPDATE_PIN',
                })
            }),
            mergeMap(() => of(true)),
            catchError((error) =>
                throwError(
                    new VoltError(
                        error.code === VoltError.codes.UNKNOWN_API_ERROR.code
                            ? VoltError.codes.UPDATE_PIN_FAILED
                            : error,
                        {
                            inheritedError: error,
                        }
                    )
                )
            )
        )
    }
}

/**
 * @typedef {Object} PinType
 * @property {String} MASTER - PurchaseApi
 * @property {String} ADULT - ParentalApi
 * @property {String} CENSORSHIP - R21Pin
 */

/**
 * @typedef {Object} RequestHeaders
 * @property {String} Content-Type - request's content type
 * @property {String} nv-tenant-id - nagra tenant id
 */

/**
 * @typedef {Object} UserPinData
 * @property {String} accountId - user's account id (TV id or ESSO id)
 * @property {PinType} type - {@link PinType}
 * @property {String} value - user's pin
 * @property {String} salt
 * @property {Date} lastChangeDateTime - user's pin change date
 */

/**
 * @typedef {Object} GetUserPinResponse
 * @property {Number} totalRecords - count of user's pins
 * @property {Array<UserPinData>} resourceSet - user's pin
 */

/**
 * For some reason nagra API returns response code 200 when there is an error
 * @typedef {Object} NagraSpecificBackendError
 * @property {String} localeMessage - some message
 * @property {Number} requestId - request id
 * @property {String} result - error description
 * @property {String} resultCode - specific nagra backend error code
 */
